import {
  Button,
  Checkbox,
  Classes,
  Dialog,
  DialogProps,
  FormGroup,
  MenuItem,
} from "@blueprintjs/core";
import { gql, useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { Select } from "@blueprintjs/select";
import { BasicTable } from "../../components/shared/Table/BasicTable";

type GetFulfillmentsResult = {
  fulfillments: {
    id: number;
    lineItems: {
      id: number;
    }[];
    createdAt: string;
  }[];
};

const GetFulfillments = gql`
  query GetFulfillments {
    fulfillments(order: { createdAt: DESC }) {
      id
      lineItems {
        id
      }
      createdAt
    }
  }
`;

const FulfillmentSelect =
  Select.ofType<GetFulfillmentsResult["fulfillments"][0]>();

type AttachFulfillmentDialogProps = DialogProps & {
  onAttach: (fulfillmentId: number, lineItemIds: number[]) => void;
};

export function AttachFulfillmentDialog({
  onAttach,
  ...dialogProps
}: AttachFulfillmentDialogProps) {
  const getFulfillments = useQuery<GetFulfillmentsResult>(GetFulfillments);
  const [selectedFulfillment, setSelectedFulfillment] =
    useState<GetFulfillmentsResult["fulfillments"][0]>();
  const [selectedLineItems, setSelectedLineItems] = useState<
    GetFulfillmentsResult["fulfillments"][0]["lineItems"][0][]
  >([]);

  useEffect(() => {
    setSelectedLineItems([]);
  }, [selectedFulfillment?.id]);

  return (
    <Dialog title="Attach Fulfillment" {...dialogProps}>
      <div className={Classes.DIALOG_BODY}>
        <FormGroup label="Fulfillment">
          <FulfillmentSelect
            items={getFulfillments.data?.fulfillments ?? []}
            itemRenderer={(fulfillment, { handleClick }) => (
              <MenuItem text={`F-${fulfillment.id}`} onClick={handleClick} />
            )}
            itemPredicate={(query, fulfillment) =>
              fulfillment.id.toString().includes(query)
            }
            onItemSelect={(fulfillment) => setSelectedFulfillment(fulfillment)}
            popoverProps={{ minimal: true, targetClassName: "w-full" }}
          >
            <Button
              text={
                selectedFulfillment
                  ? `F-${selectedFulfillment.id}`
                  : "Select Fulfillment"
              }
              rightIcon="chevron-down"
              alignText="left"
              fill
            />
          </FulfillmentSelect>
        </FormGroup>

        {selectedFulfillment && selectedFulfillment.lineItems.length > 0 && (
          <FormGroup label="Line Items">
            <BasicTable>
              <thead>
                <tr>
                  <th style={{ width: 25 }}>
                    <Checkbox
                      style={{ margin: 0 }}
                      checked={
                        selectedLineItems.length > 0 &&
                        selectedLineItems.length ===
                          selectedFulfillment.lineItems.length
                      }
                      indeterminate={
                        selectedLineItems.length > 0 &&
                        selectedLineItems.length <
                          selectedFulfillment.lineItems.length
                      }
                      onClick={() => {
                        if (selectedLineItems.length > 0) {
                          setSelectedLineItems([]);
                        } else {
                          setSelectedLineItems(selectedFulfillment.lineItems);
                        }
                      }}
                    />
                  </th>
                  <th>SO #</th>
                  <th>PO #</th>
                  <th>Inventory #</th>
                  <th>Product Code</th>
                </tr>
              </thead>
              <tbody>
                {selectedFulfillment.lineItems.map((lineItem) => (
                  <tr key={lineItem.id}>
                    <td>
                      <Checkbox
                        style={{ margin: 0 }}
                        checked={selectedLineItems.some(
                          (selectedLineItem) =>
                            selectedLineItem.id === lineItem.id
                        )}
                        onChange={(e) => {
                          if ((e.target as any).checked) {
                            setSelectedLineItems([
                              ...selectedLineItems,
                              lineItem,
                            ]);
                          } else {
                            setSelectedLineItems(
                              selectedLineItems.filter(
                                (selectedLineItem) =>
                                  selectedLineItem.id !== lineItem.id
                              )
                            );
                          }
                        }}
                      />
                    </td>
                    <td>{lineItem.id}</td>
                  </tr>
                ))}
              </tbody>
            </BasicTable>
          </FormGroup>
        )}
      </div>

      <div className={Classes.DIALOG_FOOTER}>
        <div className={Classes.DIALOG_FOOTER_ACTIONS}>
          <Button text="Cancel" onClick={dialogProps.onClose} />
          <Button
            intent="primary"
            onClick={() =>
              onAttach(
                selectedFulfillment!.id,
                selectedLineItems.map((li) => li.id)
              )
            }
            disabled={!selectedFulfillment}
          >
            {selectedFulfillment
              ? selectedLineItems.length > 0
                ? `Attach F-${selectedFulfillment.id} and ${selectedLineItems.length} Line Items`
                : `Attach F-${selectedFulfillment.id}`
              : "Attach"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
